/**
 * ContentFSelectSourceModal
 *
 * Modal for selecting a Content CP session as the card source for a new Content F session.
 * Displayed when the user starts a new Content F session.
 */

import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, FileText } from 'lucide-react'
import { useContentFStore, type ContentCpSessionSummary } from '@/stores/useContentFStore'
import { formatDistanceToNow } from 'date-fns'

interface ContentFSelectSourceModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (sessionId: string) => void
}

export function ContentFSelectSourceModal({
  isOpen,
  onClose,
  onSelect,
}: ContentFSelectSourceModalProps) {
  const {
    contentCpSessions,
    isLoadingContentCpSessions,
    loadContentCpSessions,
  } = useContentFStore()

  // Load content_cp sessions when modal opens
  useEffect(() => {
    if (isOpen) {
      loadContentCpSessions()
    }
  }, [isOpen, loadContentCpSessions])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSessionClick = (session: ContentCpSessionSummary) => {
    onSelect(session.session_id)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/10 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-background border border-border w-full max-w-md mx-4 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-4">
          <h2 className="font-light text-lg">Select Card Library</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Choose a Content CP session to use as your card source.
          </p>
        </div>

        {/* Session List */}
        <div className="px-6 pb-6 max-h-[50vh] overflow-y-auto overscroll-contain">
          {isLoadingContentCpSessions ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : contentCpSessions.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" strokeWidth={1.5} />
              <p className="text-muted-foreground text-sm">
                No Content CP sessions found.
              </p>
              <p className="text-muted-foreground/70 text-xs mt-1">
                Create a session in Content CP first.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {contentCpSessions.map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => handleSessionClick(session)}
                  className="w-full text-left border border-border rounded-md p-4 hover:bg-muted/50 transition-colors active:scale-[0.98]"
                >
                  <div className="font-light">
                    {session.name || 'Untitled Session'}
                  </div>
                  <div className="text-muted-foreground text-sm mt-1 flex items-center gap-2">
                    <span>{session.card_count} cards</span>
                    <span className="text-muted-foreground/50">·</span>
                    <span>
                      Updated {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-light border border-border rounded-md hover:bg-muted/50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
